import { format as fnsFormat, formatRelative, fromUnixTime, endOfDay, startOfDay } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'

export const DAY_WIRE_FORMAT = 'yyyy-MM-dd'

const BCP47_BY_LANGUAGE: Record<string, string> = {
  en: 'en-US',
  'pt-BR': 'pt-BR',
}

export const DateService = {
  format(date: Date | number, formatStr: string): string {
    return fnsFormat(date, formatStr)
  },

  formatUnix(unixTimestamp: number, formatStr: string): string {
    return fnsFormat(fromUnixTime(unixTimestamp), formatStr)
  },

  calendar(date: Date | number, baseDate: Date = new Date()): string {
    return formatRelative(date, baseDate)
  },

  timezone(date: Date | number, tz: string, formatStr: string): string {
    return formatInTimeZone(date, tz, formatStr)
  },

  parseDay(day: string): Date | null {
    const parsed = new Date(`${day}T00:00:00`)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  },

  formatDayWire(date: Date): string {
    return fnsFormat(date, DAY_WIRE_FORMAT)
  },

  dayBoundary(day: string, boundary: 'start' | 'end'): string | undefined {
    const parsed = DateService.parseDay(day)
    if (!parsed) return undefined
    const bounded = boundary === 'start' ? startOfDay(parsed) : endOfDay(parsed)
    return fnsFormat(bounded, "yyyy-MM-dd'T'HH:mm:ssxxx")
  },

  dayPickerFormat(language?: string): string {
    return BCP47_BY_LANGUAGE[language ?? ''] === 'pt-BR' ? 'dd/MM/yyyy' : 'MM/dd/yyyy'
  },

  formatDate(value: string, language?: string): string | null {
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return null
    return parsed.toLocaleDateString(BCP47_BY_LANGUAGE[language ?? ''] ?? 'en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  },

  formatDayLabel(day: string, language?: string): string | null {
    const parsed = DateService.parseDay(day)
    if (!parsed) return null
    return parsed.toLocaleDateString(BCP47_BY_LANGUAGE[language ?? ''] ?? 'en-US', {
      day: '2-digit',
      month: '2-digit',
    })
  },
} as const
